// Responder um questionário (/e/quiz/:id) e ver as respostas (/c/quiz/:id/answers):
// validação, o corpo que a API recebe e o resumo por pergunta. As respostas guardam
// texto: a escrita, o que a pessoa digitou; a múltipla escolha, o título da opção.

/** Perguntas na ordem em que o ADM criou (a do editor). */
export const sortQuestions = (questions) => [...(questions || [])].sort((a, b) => a.id - b.id);

export const isMultiple = (question) => question?.question_type === "multi";

/** Opções de múltipla escolha com título (as antigas da pergunta escrita vêm vazias). */
export const questionOptions = (question) =>
  (Array.isArray(question?.options) ? question.options : []).filter((option) => option?.title?.trim());

/** Id do campo de cada pergunta, usado no erro e no foco. */
export const answerFieldId = (question) => `answer-q${question.id}`;

/** Texto de uma resposta da API (a coluna é JSON: o esperado é texto). */
export const answerText = (answer) => {
  const value = answer?.answer;
  if (value === null || value === undefined) return "";
  return typeof value === "string" ? value : JSON.stringify(value);
};

/** Erros por id do campo, na ordem da tela. Todas as perguntas são obrigatórias. */
export function validateAnswers(questions, values) {
  const errors = {};
  sortQuestions(questions).forEach((question) => {
    if ((values[question.id] || "").trim()) return;
    errors[answerFieldId(question)] = isMultiple(question) ? "Escolha uma opção." : "Responda esta pergunta.";
  });
  return errors;
}

/** Corpo do `POST /quiz-answers`, o da tela antiga: uma resposta por pergunta. */
export function answersPayload(quizId, questions, values) {
  return {
    answers: sortQuestions(questions).map((question) => ({
      quiz: Number(quizId),
      quiz_question_id: question.id,
      answer: (values[question.id] || "").trim(),
    })),
  };
}

/**
 * Respostas separadas por pergunta. As sem pergunta (gravadas antes de um conserto do
 * back) ou de uma pergunta que não está mais no questionário vão em `orphans`.
 */
export function groupAnswers(questions, answers) {
  const byQuestion = Object.fromEntries((questions || []).map((question) => [question.id, []]));
  const orphans = [];
  (answers || []).forEach((answer) => {
    const list = byQuestion[answer.quiz_question_id];
    if (list) list.push(answer);
    else orphans.push(answer);
  });
  return { byQuestion, orphans };
}

const percentOf = (count, total) => (total ? Math.round((count / total) * 100) : 0);

/**
 * Resumo da múltipla escolha: quantas escolheram cada opção (pelo texto) e, em
 * `others`, as respostas que não batem com nenhuma opção atual (opção renomeada depois).
 */
export function summarizeQuestion(question, answers) {
  const total = (answers || []).length;
  const options = questionOptions(question).map((option) => ({
    title: option.title,
    isCorrect: !!option.isCorrect,
    count: 0,
  }));
  const byTitle = Object.fromEntries(options.map((option) => [option.title, option]));
  const others = {};
  (answers || []).forEach((answer) => {
    const text = answerText(answer);
    if (byTitle[text]) byTitle[text].count += 1;
    else others[text] = (others[text] || 0) + 1;
  });
  return {
    total,
    options: options.map((option) => ({ ...option, percent: percentOf(option.count, total) })),
    others: Object.entries(others).map(([title, count]) => ({ title, count, percent: percentOf(count, total) })),
  };
}

/** "3 · 60%" */
export const countPercentLabel = ({ count, percent }) => `${count} · ${percent}%`;

/** "Nenhuma resposta", "1 resposta", "3 respostas". */
export function answersLabel(count) {
  const n = Number(count) || 0;
  if (!n) return "Nenhuma resposta";
  return `${n} ${n === 1 ? "resposta" : "respostas"}`;
}
